<template>
  <ion-page class="guide-page-root">
    <div class="pdf-container" id="pdf-container">
      <div class="spinner-wrapper" v-if="guideNumPages < 0">
        <ion-spinner name="lines"></ion-spinner>
      </div>
      <div
        id="pdf-wrapper"
        class="pdf-wrapper"
        v-if="guideNumPages >= 0 && !releaseEntity.guideOffline"
      >
        <ion-img
          v-for="page in guideNumPages"
          :key="page"
          class="guide-page"
          :id="`guide-page-${page}`"
          :src="`${guidePath}/${page > 9 ? page : '0' + page}${guideToken ? '?token=' + encodeURIComponent(guideToken) : ''}`"
          @click="setLinksContainerVisibility()"
        ></ion-img>

        <div class="ban-container" v-show="guideNumPages == 0">
          <ion-icon slot="icon-only" :icon="ban"></ion-icon>
        </div>
      </div>
      <div
        id="pdf-wrapper"
        class="pdf-wrapper"
        v-if="guideNumPages >= 0 && releaseEntity.guideOffline"
      >
        <ion-img
          class="guide-page"
          v-for="(page, idx) in guidePages"
          :key="page.name"
          :src="`${guidePath}/${page.name}${guideToken ? '?token=' + encodeURIComponent(guideToken) : ''}`"
          :id="`guide-page-${idx + 2}`"
          @click="setLinksContainerVisibility()"
        ></ion-img>

        <div class="ban-container" v-show="guideNumPages == 0">
          <ion-icon slot="icon-only" :icon="ban"></ion-icon>
        </div>
      </div>

      <div
        class="links-container"
        v-show="showLinksContainer || !showScrollTop"
      >
        <div class="guide-link" @click="scrollTop()" v-show="showScrollTop">
          <ion-icon slot="icon-only" :icon="arrowUpOutline"></ion-icon>
        </div>
        <div class="guide-link" @click="expandLinks = !expandLinks" v-show="false">
          <ion-icon slot="icon-only" :icon="menu"></ion-icon>
        </div>
        <div
          v-show="expandLinks"
          class="guide-link"
          v-for="(link, idx) in links"
          :key="idx"
          @click="scrollTop(link.destPage)"
        >
          {{ idx + 1 }}
        </div>
      </div>
    </div>
  </ion-page>
</template>

<script lang="ts">
import { GlobalContext } from "@/model/globalcontext.model";
import { GoogleMedia } from "@/model/googlemedia.model";
import { GuideLink } from "@/model/guide.link.model";
import { PlayerModes } from "@/model/player.mode";
import { PlayerBar } from "@/model/playerbar.model";
import { Release } from "@/model/release.model";
import FilesystemCustomUtils from "@/services/filesystem.custom.utils";
import GestureService from "@/services/gesture.service";
import { KeepAwake } from "@capacitor-community/keep-awake";
import { Capacitor, CapacitorHttp } from "@capacitor/core";
import {
  Directory,
  Encoding,
  FileInfo,
  Filesystem,
} from "@capacitor/filesystem";
import { Preferences } from "@capacitor/preferences";
import { IonIcon, IonImg, IonPage, IonSpinner } from "@ionic/vue";
import {
  addCircle,
  arrowUpOutline,
  ban,
  menu,
  removeCircle,
} from "ionicons/icons";
import { defineComponent, inject, ref } from "vue";

export default defineComponent({
  name: "ReleaseGuide",
  components: {
    IonPage,
    IonImg,
    IonSpinner,
    IonIcon,
  },

  setup() {
    const pdfContainer = ref();
    const pdfViewerRef = ref();
    const releaseEntity = inject("release") as Release;
    const playerBar = inject("playerBar") as PlayerBar;
    const globalContext = inject("globalContext") as GlobalContext;
    const isMobile = !globalContext.isMobile;
    const gestureService = new GestureService();
    const fsUtils = new FilesystemCustomUtils();

    return {
      pdfContainer,
      pdfViewerRef,
      releaseEntity,
      playerBar,
      addCircle,
      removeCircle,
      ban,
      arrowUpOutline,
      isMobile,
      globalContext,
      gestureService,
      fsUtils,
      menu,
    };
  },

  data() {
    return {
      pdfData: "",
      guideList: Array<GoogleMedia>(),
      guidePath: "",
      guideToken: "",
      guideNumPages: -1,
      guideNumPagesVisible: Array<number>(),
      guidePages: Array<FileInfo>(),
      links: Array<GuideLink>(),
      showScrollTop: false,
      showLinksContainer: true,
      expandLinks: false,
    };
  },

  ionViewWillEnter() {
    if (
      this.playerBar.visible ||
      this.playerBar.musicList.length > 0 ||
      this.playerBar.playing
    ) {
      this.playerBar.microMode = true;
      this.playerBar.microExtendedMode = false;
      this.playerBar.currentMode = PlayerModes.MICRO;
    }

    async () => {
      await KeepAwake.keepAwake();
    };

    this.$nextTick(() => {
      this.loadGuideAsImages();
    });
  },

  ionViewDidLeave() {
    if (
      this.playerBar.visible ||
      this.playerBar.musicList.length > 0 ||
      this.playerBar.playing
    ) {
      this.playerBar.microMode = false;
      this.playerBar.microExtendedMode = false;
      this.playerBar.currentMode = PlayerModes.EXTENDED;
    }

    async () => {
      await KeepAwake.allowSleep();
    };
  },

  watch: {
    guideNumPages(newVal: number) {
      if (newVal > 0) {
        this.$nextTick(() => {
          const guideWrapper = document.getElementById("pdf-wrapper");
          const parent = document.getElementById("pdf-container");
          if (guideWrapper) {
            this.gestureService.enablePinchZoom(
              guideWrapper as HTMLElement,
              parent as HTMLElement
            );

            guideWrapper.onscroll = () => {
              this.showScrollTop = guideWrapper.scrollTop > 400;
            };
          }
        });
      }
    },
  },

  methods: {
    setLinksContainerVisibility() {
      this.showLinksContainer = !this.showLinksContainer;
    },
    range(start: number, end: number) {
      return Array(end - start + 1)
        .fill(1)
        .map((_, idx) => start + idx);
    },
    scrollTop(pageNum?: any): void {
      const guideWrapper = document.getElementById("pdf-wrapper");
      if (guideWrapper && !pageNum) {
        guideWrapper.scrollTo({
          top: 0,
          left: 0,
          behavior: "smooth",
        });
      } else if (pageNum) {
        const destPage = document.getElementById(
          `guide-page-${pageNum ? Number(pageNum) + 1 : 2}`
        );
        if (guideWrapper && destPage) {
          guideWrapper.scrollTo({
            top: destPage.offsetTop,
            left: 0,
            behavior: "smooth",
          });
        }
      }
    },

    async loadGuideAsImages() {
      // Check if release uses server storage
      if (this.releaseEntity.contentSource === 'SERVER') {
        // Use server API
        if (this.releaseEntity.guideOffline) {
          // Load offline guide
          console.log("Found offline directory: " + this.releaseEntity.guideDirectory);

          Filesystem.readdir({
            path: this.releaseEntity.guideDirectory,
            directory: Directory.Data,
          })
            .then((response) => {
              Filesystem.getUri({
                path: `${this.releaseEntity.guideDirectory}`,
                directory: Directory.Data,
              }).then((res) => {
                this.guidePath = `${Capacitor.convertFileSrc(res.uri)}`;

                this.guidePages = response.files
                  .filter((f) => {
                    return f.name.endsWith("png");
                  })
                  .sort((a, b) => {
                    let sorted = 0;
                    if (a.name < b.name) sorted = -1;
                    else if (a.name > b.name) sorted = 1;
                    return sorted;
                  });
                this.guideNumPages = this.guidePages.length;

                const guideLinks = response.files.find((f) => {
                  return f.name === this.fsUtils.GUIDE_LINKS_PATH;
                });

                if (undefined !== guideLinks) {
                  Filesystem.readFile({
                    path: `${this.releaseEntity.guideDirectory}/${guideLinks.name}`,
                    directory: Directory.Data,
                    encoding: Encoding.UTF8,
                  }).then((linksResponse) => {
                    try {
                      this.links = JSON.parse(typeof linksResponse.data === 'string' ? linksResponse.data : '');
                      this.releaseEntity.links = this.links;
                    } catch (ex) {
                      console.error("Error extracting links", ex);
                    }
                  });
                }
              });
            })
            .catch(() => {
              this.guideNumPages = 0;
            });
        } else {
          // Load from server API
          const tokenQuery = await Preferences.get({ key: "jwt" });
          const token = tokenQuery.value;

          const options = {
            url: `${process.env.VUE_APP_API_URL}api/releases/${this.releaseEntity.id}/guide/pages`,
            headers: { Authorization: `Bearer ${token}` },
            readTimeout: 1000000,
          };

          await CapacitorHttp.get(options)
            .then((response) => {
              // Store base path and token separately
              this.guidePath = `${process.env.VUE_APP_API_URL}api/releases/${this.releaseEntity.id}/guide/pages`;
              this.guideToken = token || '';
              this.guideNumPages = Number(response.data.pages);
              this.releaseEntity.guideNumPages = this.guideNumPages;
            })
            .catch(() => {
              console.error("Failed to load guide");
              this.guideNumPages = 0;
            });
        }
        return;
      }

      // Original Google Drive logic (for backward compatibility)
      if (this.releaseEntity.guideOffline) {
        //load offline guide
        console.log(
          "Found offline directory: " + this.releaseEntity.guideDirectory
        );

        Filesystem.readdir({
          path: this.releaseEntity.guideDirectory,
          directory: Directory.Data,
        })
          .then((response) => {
            Filesystem.getUri({
              path: `${this.releaseEntity.guideDirectory}`,
              directory: Directory.Data,
            }).then((res) => {
              this.guidePath = `${Capacitor.convertFileSrc(res.uri)}`;

              this.guidePages = response.files
                .filter((f) => {
                  return f.name.endsWith("png");
                })
                .sort((a, b) => {
                  let sorted = 0;
                  if (a.name < b.name) sorted = -1;
                  else if (a.name > b.name) sorted = 1;
                  return sorted;
                });
              this.guideNumPages = this.guidePages.length;

              const guideLinks = response.files.find((f) => {
                return f.name === this.fsUtils.GUIDE_LINKS_PATH;
              });

              if (undefined !== guideLinks) {
                Filesystem.readFile({
                  path: `${this.releaseEntity.guideDirectory}/${guideLinks.name}`,
                  directory: Directory.Data,
                  encoding: Encoding.UTF8,
                }).then((linksResponse) => {
                  try {
                    this.links = JSON.parse(typeof linksResponse.data === 'string' ? linksResponse.data : '');
                    this.releaseEntity.links = this.links;
                  } catch (ex) {
                    console.error("Error extracting links", ex);
                  }
                });
              }
            });
          })
          .catch(() => {
            // not exists or not a dir
            this.guideNumPages = 0;
          });
      } else {
        const tokenQuery = await Preferences.get({ key: "jwt" });
        const token = tokenQuery.value;

        const options = {
          url: `${process.env.VUE_APP_API_URL}api/google/media/${
            this.releaseEntity.programShortName +
            "_" +
            this.releaseEntity.releaseWithZero +
            "_Guide"
          }`,
          headers: { Authorization: `Bearer ${token}` },
          readTimeout: 1000000,
        };

        await CapacitorHttp.get(options)
          .then((response) => {
            this.guideList = response.data;

            this.guidePath =
              this.guideList.length > 0
                ? `${process.env.VUE_APP_API_EXT_URL}api/google/guide/pages/${this.guideList[0].id}`
                : "";

            if (this.releaseEntity.guideNumPages == 0) {
              const pagesOptions = {
                url: `${process.env.VUE_APP_API_EXT_URL}api/google/guide/pages-with-links/${this.guideList[0].id}`,
                headers: { Authorization: `Bearer ${token}` },
                readTimeout: 1000000,
              };

              CapacitorHttp.get(pagesOptions).then((response) => {
                try {
                  if (response.data.links) {
                    this.links = JSON.parse(response.data.links);
                    this.releaseEntity.links = this.links;
                  }
                } catch (ex) {
                  console.error("Error extracting links", ex);
                } finally {
                  this.guideNumPages = Number(response.data.pages);
                  this.releaseEntity.guideNumPages = this.guideNumPages;
                }
              });
            } else {
              this.guideNumPages = this.releaseEntity.guideNumPages;
              this.links = this.releaseEntity.links;
            }
          })
          .catch(() => {
            console.error("Failed to load guide");
            this.guideNumPages = 0;
          });
      }
    },

    async getGuide(): Promise<string> {
      const tokenQuery = await Preferences.get({ key: "jwt" });
      const token = tokenQuery.value;

      return new Promise<string>((resolve) => {
        if (this.releaseEntity.guideOffline) {
          //load offline guide
          console.log(
            "Found offline directory: " + this.releaseEntity.guideDirectory
          );

          Filesystem.readdir({
            path: this.releaseEntity.guideDirectory,
            directory: Directory.Data,
          })
            .then((response) => {
              const firstGuide = response.files.find((f) => {
                return f.name.endsWith("pdf");
              });
              if (undefined !== firstGuide) {
                Filesystem.getUri({
                  path: `${this.releaseEntity.guideDirectory}/${firstGuide.name}`,
                  directory: Directory.Data,
                }).then((res) => {
                  this.guidePath = Capacitor.convertFileSrc(res.uri);
                  resolve(this.guidePath);
                });
              } else {
                resolve("");
              }
            })
            .catch(() => {
              // not exists or not a dir
              resolve("");
            });
        } else {
          const options = {
            url: `${process.env.VUE_APP_API_URL}api/google/media/${
              this.releaseEntity.programShortName +
              "_" +
              this.releaseEntity.releaseWithZero +
              "_Guide"
            }`,
            headers: { Authorization: `Bearer ${token}` },
            readTimeout: 60000,
          };

          CapacitorHttp.get(options)
            .then(async (response) => {
              this.guideList = response.data;

              this.guidePath =
                this.guideList.length > 0
                  ? `${process.env.VUE_APP_API_EXT_URL}api/google/guide/${this.guideList[0].id}`
                  : "";

              resolve(this.guidePath);
            })
            .catch((ex) => {
              console.error(ex);
              resolve("");
            });
        }
      });
    },
  },
});
</script>

<style scoped>
.guide-page-root {
  width: 100%;
  max-width: 100%;
  height: 100%;
  overflow: hidden;
}

.pdf-container {
  --background: #353839;
  background: #353839;
  /* Не использовать 100vw: внутри split-pane это шире видимой колонки */
  width: 100%;
  max-width: 100%;
  height: 100%;
  min-height: 100%;
  overflow: auto;
  scrollbar-width: thin;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  box-sizing: border-box;
}
.pdf-wrapper {
  width: min(100%, 920px);
  max-width: 100%;
  margin: 0 auto;
  overflow: visible;
  scrollbar-width: thin;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
}

.guide-page {
  width: 100%;
  max-width: 100%;
  height: auto;
  display: block;
  object-fit: contain;
  background: #fff;
}

.guide-page::part(image),
:deep(.guide-page img) {
  width: 100%;
  max-width: 100%;
  height: auto;
  display: block;
  object-fit: contain;
}

.links-container {
  z-index: 10000;
  position: absolute;
  top: 10vh;
  right: 16px;
  width: 50px;
  max-height: 70vh;
  display: flex;
  flex-flow: column;
  justify-content: flex-start;
  align-items: center;
}
.guide-link {
  width: 50px;
  height: 50px;
  background: var(--ion-color-orange-transparent);
  color: white;
  font-weight: bold;
  border-radius: 5px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 10px;
  cursor: pointer;
}
.guide-link:hover {
  background: var(--ion-color-orange-tint-transparent);
}
.spinner-wrapper {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

#guideContainer {
  position: absolute;
  overflow: auto;
  width: 100%;
  top: 5rem;
  bottom: 4rem;
  left: 50%;
  transform: translate(-50%, 0);
}

.scroll-top-btn {
  position: fixed;
  right: 20px;
  top: 5vh;
  z-index: 10000;
  width: 50px;
  height: 50px;
  --background: var(--ion-color-orange);
  color: white;
}

.ban-container {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>